import * as React from "react";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import { useListPendingLegacyCerts } from "@/hooks/queries/useListPendingLegacyCerts";
import type { PendingLegacyCertItem } from "@/hooks/queries/admin-legacy-cert-types";
import { LegacyCertReviewPanel } from "@/routes/admin/LegacyCertReviewPanel";
import { formatDate } from "@/lib/format-date";

// Story 16.4 — super_admin pending-cert verification queue (UX §13.10, AC8/9/11/12).
// A dedicated route (/admin/legacy-certs, super_admin-only — Decision 2) until the
// real Screen 10 console exists. Lists the legacy AMPS certificates awaiting
// verification (table cols-5: Clinician / Email / Organization / Uploaded / Action);
// a per-row Review opens the panel where the cert is previewed and approved. On a
// successful approval the queue refetches and the decided row drops out (AC9).
//
// Tokens only; no pure-white background, no hardcoded hex (CLAUDE.md constraints).

export function LegacyCertQueueScreen() {
  const { data, isPending, isError, refetch } = useListPendingLegacyCerts();
  const [reviewing, setReviewing] = React.useState<PendingLegacyCertItem | null>(null);

  const rows = data?.results ?? [];
  // The queue is a single capped page (PENDING_CERTS_PAGE_SIZE). If the backlog ever
  // exceeds the cap, say so explicitly rather than silently hiding the overflow.
  const total = data?.count ?? rows.length;
  const hasOverflow = total > rows.length;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">
          Certificate verification
        </h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Legacy AMPS clinicians awaiting certificate verification. Review each
          certificate, then approve it to advance the clinician.
        </p>
      </header>

      {isError ? (
        <Banner variant="danger" title="Couldn't load the verification queue">
          <div className="flex flex-col gap-2">
            <span>We couldn&apos;t load the pending certificates. Please try again.</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading pending certificates…
        </div>
      ) : rows.length === 0 && !isError ? (
        <Banner variant="info" title="All caught up">
          No certificates awaiting verification.
        </Banner>
      ) : rows.length > 0 ? (
        <div className="flex flex-col gap-3">
          <QueueTable rows={rows} onReview={setReviewing} />
          {hasOverflow ? (
            <Banner variant="info" title="More certificates pending">
              Showing the first {rows.length} of {total} pending certificates. Approve
              some to reveal the rest.
            </Banner>
          ) : null}
        </div>
      ) : null}

      {reviewing ? (
        <LegacyCertReviewPanel
          cert={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => setReviewing(null)}
        />
      ) : null}
    </section>
  );
}

function QueueTable({
  rows,
  onReview,
}: {
  rows: PendingLegacyCertItem[];
  onReview: (cert: PendingLegacyCertItem) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Legacy certificates awaiting verification</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              Clinician
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Email
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Organization
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Uploaded
            </th>
            <th scope="col" className="px-4 py-3 text-right font-semibold">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0"
            >
              <td className="px-4 py-3 font-medium text-[color:var(--text-primary)]">
                {row.clinician_name ?? "—"}
              </td>
              <td className="px-4 py-3 text-[color:var(--text-secondary)]">{row.email}</td>
              <td className="px-4 py-3 text-[color:var(--text-secondary)]">
                {row.organization ?? "—"}
              </td>
              <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                {formatDate(row.uploaded_at)}
              </td>
              <td className="px-4 py-3 text-right">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onReview(row)}
                  aria-label={`Review certificate for ${row.clinician_name ?? row.email}`}
                >
                  Review
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
